import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, TrendingUp, Building2, Award, MapPin, ExternalLink } from "lucide-react";
import { usePageSEO } from "@/hooks/usePageSEO";

const CASE_NARRATIVES = [
  {
    title: "Federal Data Operations: U.S. Department of Commerce",
    challenge: "The National Hospital Ambulatory Medical Care Surveys required meticulous data collection, quality assurance, and coordination across multiple healthcare facilities.",
    approach: "Deployed systematic data collection workflows, implemented quality control checkpoints, and maintained precise statistical standards across survey operations in the Philadelphia region.",
    outcome: "Successfully managed survey operations for 2+ years, contributing to national healthcare data that informs federal policy decisions.",
    tags: ["Government", "Data Operations", "Healthcare"],
    period: "2019 – 2021",
    location: "Philadelphia, PA"
  },
  {
    title: "Enterprise IT Transformation: Resources for Human Development",
    challenge: "A multi-site human services organization needed modernized IT infrastructure, standardized processes, and improved system reliability across operations.",
    approach: "Led technology strategy overhaul including network infrastructure upgrades, system integration, helpdesk optimization, and staff training programs across multiple facilities.",
    outcome: "5 years of IT leadership resulting in improved system uptime, reduced support tickets, and a scalable technology foundation supporting organizational growth.",
    tags: ["IT Management", "Nonprofit", "Infrastructure"],
    period: "2012 – 2017",
    location: "Philadelphia, PA"
  },
  {
    title: "Telecom Support Excellence: Comcast Business",
    challenge: "Business customers required responsive, technically proficient support for complex billing and IT issues across enterprise accounts.",
    approach: "Combined deep technical knowledge with customer-first billing support, resolving complex cases and improving client satisfaction through systematic troubleshooting.",
    outcome: "3 years of consistent high-performance ratings in both IT support and billing resolution, serving enterprise business accounts.",
    tags: ["Enterprise", "IT Support", "Telecom"],
    period: "2011 – 2014",
    location: "Horsham, PA"
  },
  {
    title: "Automotive Executive Operations: General Motors",
    challenge: "Regional executive leadership needed seamless administrative support, calendar management, and operational coordination across multiple divisions.",
    approach: "Provided executive-level administrative support with precision scheduling, cross-divisional coordination, and proactive problem-solving for regional leadership.",
    outcome: "2+ years of trusted executive partnership supporting regional operations for one of the world's largest automotive manufacturers.",
    tags: ["Automotive", "Executive Support", "Fortune 500"],
    period: "2008 – 2010",
    location: "Paoli, PA"
  }
];

export const CaseNarrativesPage = () => {
  const navigate = useNavigate();
  usePageSEO("Case Studies & Narratives", "Real-world consulting case studies across government, enterprise, and Fortune 500 engagements. U.S. Dept of Commerce, Comcast Business, General Motors.");

  return (
    <div className="min-h-screen bg-white" data-testid="case-narratives-page">
      {/* Header */}
      <header className="bg-[#333333] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" />
            <span className="text-sm text-neutral-400">Case Narratives</span>
          </Link>
          <Button variant="ghost" className="text-neutral-300 hover:text-white" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-neutral-50 py-14 border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="w-14 h-14 bg-[#39FF14]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <TrendingUp className="w-7 h-7 text-[#2BC40E]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-3">Case Narratives</h1>
          <p className="text-neutral-600 text-lg">
            Real-world experience across government, enterprise, and Fortune 500 engagements that drive our consulting methodology.
          </p>
        </div>
      </section>

      {/* Cases */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="space-y-6">
          {CASE_NARRATIVES.map((cn) => (
            <Card key={cn.title} className="border-neutral-200 hover:shadow-sm transition-shadow" data-testid={`case-${cn.title.slice(0, 15).replace(/\s/g, '-').toLowerCase()}`}>
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <h2 className="text-lg font-semibold text-neutral-900">{cn.title}</h2>
                  <div className="flex items-center gap-2 text-xs text-neutral-500 flex-shrink-0">
                    <span>{cn.period}</span>
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{cn.location}</span>
                  </div>
                </div>

                <div className="space-y-4 text-sm">
                  <div className="bg-red-50/50 rounded-lg p-4 border-l-3 border-l-red-300">
                    <p className="font-medium text-neutral-800 mb-1">Challenge</p>
                    <p className="text-neutral-600">{cn.challenge}</p>
                  </div>
                  <div className="bg-blue-50/50 rounded-lg p-4 border-l-3 border-l-blue-300">
                    <p className="font-medium text-neutral-800 mb-1">Approach</p>
                    <p className="text-neutral-600">{cn.approach}</p>
                  </div>
                  <div className="bg-green-50/50 rounded-lg p-4 border-l-3 border-l-green-400">
                    <p className="font-medium text-[#2BC40E] mb-1">Outcome</p>
                    <p className="text-neutral-600">{cn.outcome}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-5">
                  {cn.tags.map(t => (
                    <Badge key={t} variant="outline" className="text-xs border-neutral-200">{t}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Award */}
        <a href="https://techbehemoths.com/awards-2025/wordpress/united-states#view=44749" target="_blank" rel="noopener noreferrer"
          className="mt-12 block bg-gradient-to-r from-amber-50 to-amber-100/30 rounded-2xl p-8 border border-amber-200 text-center hover:shadow-md transition-shadow group">
          <Award className="w-10 h-10 text-amber-600 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-neutral-900 mb-2 group-hover:text-amber-700 transition-colors flex items-center justify-center gap-2">
            Tech Behemoths Award Recipient <ExternalLink className="w-4 h-4 opacity-50" />
          </h3>
          <p className="text-sm text-neutral-600 max-w-md mx-auto">
            Recognized for excellence in technology consulting and digital transformation services.
          </p>
        </a>

        {/* CTAs */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate('/capability-statement')} variant="outline" className="border-neutral-300">
            <Building2 className="w-4 h-4 mr-2" /> View Capability Statement
          </Button>
          <Button onClick={() => navigate('/clarity-assessment')} className="bg-neutral-900 hover:bg-neutral-800 text-white">
            Start Free Assessment <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default CaseNarrativesPage;
